// ABOUTME: Order-level shipment status label showing the least-far tracking status across shipments.
// ABOUTME: Hover popover lists every shipment with its own status and shipper tracking events.

import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import SHQuickAction from "../../../../theme/Modal/SHQuickAction";
import ShLabel from "../../../../theme/label/ShLabel";
import Text from "../../../../theme/text/Text";
import { getShipmentStatusLabeldata } from "../../../../../utils/labelColorText";
import { getLeastFarShipmentStatus } from "../../../../../utils/shipmentStatus";

const OrderShipmentsStatusLabel = ({ shipments }) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const shipmentsWithStatus = (shipments ?? []).filter((shipment) => shipment?.shipmentStatus);
    if (shipmentsWithStatus.length === 0) return null;

    const leastFarStatus = getLeastFarShipmentStatus(shipmentsWithStatus);
    const { labelcolor, labeltext } = getShipmentStatusLabeldata(leastFarStatus, theme);

    const label = (
        <ShLabel size={32} fitted palette="normal" variant={labelcolor}>
            {t(labeltext)}
        </ShLabel>
    );

    const hasEvents = shipmentsWithStatus.some((shipment) => shipment.shipperEvents?.length > 0);
    if (shipmentsWithStatus.length === 1 && !hasEvents) {
        return label;
    }

    return (
        <SHQuickAction
            maxWidth={700}
            anchorOrigin={{ vertical: "center", horizontal: "left" }}
            transformOrigin={{ vertical: "center", horizontal: "right" }}
            sx={{
                maxHeight: "calc(100vh - 32px)",
                overflowY: "auto",
                // The triangle only knows top/bottom placement; it has no valid spot on a side popover.
                modal: { triangle: { display: "none" } },
            }}
            component={
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        pt: 2,
                        px: 2,
                    }}
                >
                    {shipmentsWithStatus.map((shipment, shipmentIndex) => {
                        const shipmentLabelData = getShipmentStatusLabeldata(shipment.shipmentStatus, theme);
                        return (
                            <Box
                                key={shipmentIndex + "shipment"}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    pb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                    }}
                                >
                                    <Text bold>{shipment.trackingNumber ?? t("shipment")}</Text>
                                    {shipment.shipper?.name && (
                                        <Text light>{shipment.shipper.name}</Text>
                                    )}
                                    <ShLabel size={32} fitted palette="normal" variant={shipmentLabelData.labelcolor}>
                                        {t(shipmentLabelData.labeltext)}
                                    </ShLabel>
                                </Box>

                                {shipment.shipperEvents?.length > 0 && (
                                    <>
                                        <Box
                                            sx={{
                                                position: "relative",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 2,
                                            }}
                                        >
                                            <Text
                                                bold
                                                sx={{ width: "140px", flexShrink: 0 }}
                                            >
                                                {t("date")}
                                            </Text>
                                            <Text
                                                bold
                                                sx={{ width: "140px", flexShrink: 0 }}
                                            >
                                                {t("type")}
                                            </Text>
                                            <Text
                                                bold
                                                sx={{ minWidth: "350px", flexShrink: 0 }}
                                            >
                                                {t("status")}
                                            </Text>
                                        </Box>

                                        {shipment.shipperEvents.map((event, index) => (
                                            <Box
                                                key={index + "event"}
                                                sx={{
                                                    position: "relative",
                                                    minHeight: 40,
                                                    py: 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 2,
                                                }}
                                            >
                                                <Text sx={{ width: "140px", flexShrink: 0 }}>
                                                    {event.timeStamp ? new Date(event.timeStamp).toLocaleString(i18n.language) : "-"}
                                                </Text>
                                                <Text sx={{ width: "140px", flexShrink: 0 }}>
                                                    {event.type}
                                                </Text>
                                                <Text sx={{ minWidth: "350px", flexShrink: 0 }}>
                                                    {event.status}
                                                </Text>
                                            </Box>
                                        ))}
                                    </>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            }
        >
            {({anchorEl, onMouseEnter, onMouseLeave, onTouchStart}) => (
                <Box
                    aria-owns={anchorEl ? "simple-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onTouchStart={onTouchStart}
                    sx={{
                        display: "inline-flex",
                    }}
                >
                    {label}
                </Box>
            )}
        </SHQuickAction>
    );
};

export default OrderShipmentsStatusLabel;
